import React from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';

import { cn } from './utils';


/**
 * Centered modal over a scrim, per the .pen "Confirmation Dialogs" (Nebda) and
 * the Invite Modal (r4BKZ): surface-1 card, hairline stroke, soft stacked
 * shadow. `DialogContent` ships two shells via the `variant` prop — `confirm`
 * (default: radius 12 / blur-40 shadow / ~440px, the compact confirmation card)
 * and `modal` (radius 14 / blur-60 shadow / ~480px, the wide invite/modal
 * shell). The backdrop uses the shared `--scrim` token. On phone it stays
 * centered but shrinks to the viewport. Built on @radix-ui/react-dialog so
 * focus trap, ESC, scroll-lock and a11y wiring come for free.
 *
 * Compose like the Radix primitive:
 *   <Dialog>
 *     <DialogTrigger asChild><Button>Open</Button></DialogTrigger>
 *     <DialogContent>
 *       <DialogHeader>
 *         <DialogTitle>Delete this part?</DialogTitle>
 *         <DialogDescription>…</DialogDescription>
 *       </DialogHeader>
 *       <DialogFooter>…</DialogFooter>
 *     </DialogContent>
 *   </Dialog>
 *
 * `Modal` is exported as an alias of `Dialog` for callers that prefer that name.
 */
const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;
const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = React.forwardRef((props, ref) => {
  const { className, ...restProps } = props;
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-scrim',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        className,
      )}
      {...restProps}
    />
  );
});
DialogOverlay.displayName = 'DialogOverlay';

/**
 * Shell sizing/elevation per the .pen. `confirm` (default) is the compact
 * confirmation card (radius 12 / blur-40 shadow / max-w 440); `modal` is the
 * wide invite/modal shell (radius 14 / blur-60 shadow / max-w 480). Consumers
 * may still override any of these via `className` (preserved by the merge).
 */
const dialogVariants = {
  confirm: 'rounded-lg shadow-dialog max-w-[440px]',
  modal: 'rounded-lg shadow-modal max-w-[480px]',
};

const DialogContent = React.forwardRef((props, ref) => {
  const {
    className, children, variant = 'confirm', ...restProps
  } = props;
  return (
    <DialogPortal>
      <DialogOverlay />

      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 flex w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
          'flex-col gap-[13px] border border-hairline bg-surface-1 p-[22px] outline-none',
          dialogVariants[variant],
          'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
          className,
        )}
        {...restProps}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = 'DialogContent';

function DialogHeader({ className, ...restProps }) {
  return <div className={cn('flex flex-col gap-1.5', className)} {...restProps} />;
}
DialogHeader.displayName = 'DialogHeader';

function DialogFooter({ className, ...restProps }) {
  return (
    <div
      className={cn('flex items-center justify-end gap-2.5 pt-1', className)}
      {...restProps}
    />
  );
}
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef((props, ref) => {
  const { className, ...restProps } = props;
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn('text-[17px] font-semibold tracking-[-0.2px] text-ink', className)}
      {...restProps}
    />
  );
});
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef((props, ref) => {
  const { className, ...restProps } = props;
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-table leading-relaxed text-ink-muted', className)}
      {...restProps}
    />
  );
});
DialogDescription.displayName = 'DialogDescription';

const Modal = Dialog;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  Modal,
};
export default Dialog;
